import {
  parseIdParam,
  CodingSummaryCard,
  DataStatusNote,
  deserify,
  formatAcademicYear,
  formatClassSection,
  formatDate,
  formatDuration,
  formatPercent,
  GIL,
  GIL_CELL,
  GilColumn,
  GilReportTable,
  LearningOutcomesCard,
  matchesReportFilters,
  NeedsAttentionCard,
  NoContentCard,
  OverviewSection,
  PercentBar,
  performanceLabel,
  ProgressMetricsCard,
  projectLabel,
  ALL_STATUSES,
  ReportFilters,
  ReportLayout,
  SectionCard,
  StatTile,
  StatusChip,
  TimeSpentCard,
  toPercent,
  useCommonServiceClientContext,
  useGilReport,
  WeeklyTrendCard,
} from '@geneo2-web/shared-ui';
import { useOpenAttentionItem } from './useOpenAttentionItem';
import { Box, Typography } from '@mui/material';
import { GilProjectStatusEnum } from '@protos/learning_management/lms.assessment.student.apis_pb';
import { GilClassProjectRow } from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import { TeacherLoginResponseType } from '@protos/user_management/ums.login.apis_pb';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  useAppSelector,
  useDeserifiedSelector,
} from '../../../reduxStore/reduxHooks';
import { GIL_Reports_Project } from '../../../routeHandling/RoutesNomenclature';
import ClassAndSubjectPicker from '../components/ClassAndSubjectPicker';

/** A Not Started row carries no completion, time or score. */
const notStarted = (row: GilClassProjectRow) =>
  row.status === GilProjectStatusEnum.GIL_PROJECT_STATUS_NOT_STARTED;

/** Dashboard 5 - one class section, every GIL project assigned to it. */
export const GilClassReport = () => {
  const navigate = useNavigate();
  const openAttentionItem = useOpenAttentionItem();
  const [searchParams, setSearchParams] = useSearchParams();
  const { LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler } =
    useCommonServiceClientContext();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);

  const userInfo = deserify(useAppSelector((state) => state.auth))
    ?.user_info as TeacherLoginResponseType | undefined;
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );

  //~ The class picked on the Lab dashboard (shared through the store) wins; the
  //~ query param only covers a deep link opened without one.
  const sectionId =
    class_subject_info?.sectionId || parseIdParam(searchParams.get('sectionId'));

  const { state, reload } = useGilReport(
    () =>
      sectionId
        ? LmsAssessmentTeacherAPIServiceV2WithStatusCodeHandler.getGilTeacherClassReport(
            { sectionId }
          ).then((response) => response.data)
        : undefined,
    [sectionId]
  );

  const report = state.status === 'success' ? state.data : undefined;
  const summary = report?.summary;
  const projects = useMemo(() => report?.projects ?? [], [report?.projects]);
  const assignedCount = summary?.projectsAssigned ?? 0;
  const completedCount = summary?.projectsCompleted ?? 0;

  const visibleProjects = useMemo(
    () =>
      projects.filter((row) =>
        matchesReportFilters(
          projectLabel(row.project),
          row.status,
          search,
          statusFilter
        )
      ),
    [projects, search, statusFilter]
  );

  const openReport = (row: GilClassProjectRow) => {
    if (row.project?.assessmentId) {
      navigate(`${GIL_Reports_Project}/${row.project.assessmentId}`);
    }
  };

  const columns: GilColumn<GilClassProjectRow>[] = [
    {
      key: 'index',
      header: '#',
      render: (_row, index) => (
        <Typography sx={GIL_CELL.index}>{index + 1}</Typography>
      ),
    },
    {
      key: 'project',
      header: 'Project',
      minWidth: 240,
      //~ The underlined name is the drill-down into the project report.
      render: (row) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography
            role="link"
            tabIndex={0}
            onClick={() => openReport(row)}
            onKeyDown={(event) => event.key === 'Enter' && openReport(row)}
            sx={GIL_CELL.link}
          >
            {projectLabel(row.project)}
          </Typography>
          <Typography sx={GIL_CELL.sub}>
            Assigned {formatDate(row.project?.assignedDate)}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusChip status={row.status} />,
    },
    {
      key: 'completion',
      header: 'Completion',
      //~ The design shows "Not Started" in place of a completion figure.
      render: (row) =>
        notStarted(row) ? (
          <Typography noWrap sx={GIL_CELL.sub}>
            Not Started
          </Typography>
        ) : (
          <PercentBar value={row.completionPercent} />
        ),
    },
    {
      key: 'time',
      //~ Per group: the group shares one lab device, so a per-student average
      //~ would only reflect whoever was logged in.
      header: 'Avg. Time / Group',
      render: (row) =>
        notStarted(row) ? '—' : formatDuration(row.avgTimePerGroupInSec),
    },
    {
      key: 'graded',
      header: 'Graded',
      render: (row) =>
        row.progressMetrics?.submittedCount
          ? `${row.progressMetrics.gradedCount}/${row.progressMetrics.submittedCount}`
          : '—',
    },
    {
      key: 'score',
      header: 'Avg. Project Score',
      render: (row) => (
        <PercentBar
          value={row.avgProjectScorePercent}
          color={GIL.green}
          trackWidth={80}
        />
      ),
    },
  ];

  return (
    <ReportLayout
      title="GIL Class Dashboard"
      description="Track progress. Build skills. Create brighter futures."
      meta={
        report && [
          {
            value: `Class ${formatClassSection(report.classSection)}`,
            color: '#E8F8EE',
            textColor: GIL.green,
            bold: true,
          },
          {
            label: 'Teacher',
            value: report.teacherName,
            color: '#FEF7E0',
            textColor: '#B06000',
          },
          { label: 'Students', value: report.studentCount ?? 0 },
          { label: 'Groups', value: report.groupCount ?? 0 },
          {
            label: 'Academic Year',
            value: formatAcademicYear(report.classSection?.academicYear),
          },
        ]
      }
      headerRight={
        <Box sx={{ display: 'flex', width: { xs: '100%', md: 'auto' } }}>
          <ClassAndSubjectPicker
            interactionEventContext="GIL_REPORTS"
            onClassSubjectClick={() => setSearchParams({}, { replace: true })}
            teacherClassSubjects={userInfo?.teachClassSubjects ?? []}
          />
        </Box>
      }
      state={sectionId ? state : { status: 'success', data: undefined }}
      onRetry={reload}
    >
      {!sectionId ? (
        <NoContentCard
          variant="info"
          icon="laboratory"
          text="Choose Class & Subject to view GIL reports"
        />
      ) : projects.length === 0 ? (
        <NoContentCard
          variant="info"
          icon="laboratory"
          text="No GIL projects have been assigned to this class yet"
        />
      ) : (
        <>
          <DataStatusNote status={report?.dataStatus} />

          <OverviewSection>
            <StatTile
              tone="green"
              icon="gil-checklist-green"
              value={assignedCount}
              label="Projects Assigned"
              caption="Keep the momentum going!"
            />
            <StatTile
              tone="blue"
              icon="gil-submitted-blue"
              value={`${completedCount}/${assignedCount}`}
              label="Projects Completed"
              caption={`${Math.round(
                toPercent(completedCount, assignedCount)
              )}% completion rate`}
            />
            <StatTile
              tone="purple"
              icon="gil-clock-purple"
              value={formatDuration(summary?.totalTimeSpent?.totalTimeInSec)}
              label="Total Time Spent"
              caption="Across all activities"
            />
            <StatTile
              tone="red"
              icon="gil-star-red"
              value={formatPercent(summary?.avgProjectScorePercent)}
              label="Avg. Project Score"
              caption={performanceLabel(summary?.avgProjectScorePercent)}
            />
          </OverviewSection>

          <NeedsAttentionCard
            items={report?.needsAttention}
            onOpen={openAttentionItem}
          />

          <ProgressMetricsCard metrics={summary?.progressMetrics} />

          <TimeSpentCard
            videoSec={summary?.totalTimeSpent?.videoActivityBookTimeInSec}
            codingSec={summary?.totalTimeSpent?.codingTimeInSec}
          />

          <CodingSummaryCard summary={summary?.codingSummary} />

          <SectionCard
            title="Project-wise Performance"
            subtitle="Track progress and performance across all assigned projects"
            action={
              <ReportFilters
                search={search}
                onSearchChange={setSearch}
                status={statusFilter}
                onStatusChange={setStatusFilter}
              />
            }
          >
            <GilReportTable
              columns={columns}
              rows={visibleProjects}
              rowKey={(row) => String(row.project?.assessmentId ?? 0)}
              emptyText="No projects match your search"
            />
          </SectionCard>

          <LearningOutcomesCard
            outcomes={report?.learningOutcomes}
            showProjects
            subtitle="How the class is doing on each learning outcome across projects"
          />

          <WeeklyTrendCard points={report?.weeklyTrend} />
        </>
      )}
    </ReportLayout>
  );
};

export default GilClassReport;
